import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { zonesAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ZoneSelector from './ZoneSelector';

const ZONE_TYPES = [
  { id: 'general', name: 'General Area' },
  { id: 'restricted', name: 'Restricted Zone' },
  { id: 'hazard', name: 'Hazard Zone' },
  { id: 'crane_zone', name: 'Crane Zone' },
  { id: 'excavation', name: 'Excavation' },
  { id: 'chemical', name: 'Chemical Handling' },
  { id: 'electrical', name: 'Electrical Hazard' },
  { id: 'confined_space', name: 'Confined Space' },
  { id: 'hot_work', name: 'Hot Work Zone' },
  { id: 'safe_zone', name: 'Safe Zone' },
];

export default function ZoneAssignmentModal({
  isOpen,
  onClose,
  cameraId,
  siteId,
  existingZoneIds = [],
  onZoneAssigned,
  currentPoints = [],
  setCurrentPoints,
  onDrawingModeChange = null, // Callback to enable/disable drawing mode in parent
}) {
  const [activeTab, setActiveTab] = useState('select'); // 'select' or 'create'
  const [newZoneName, setNewZoneName] = useState('');
  const [selectedZoneType, setSelectedZoneType] = useState('restricted');
  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();

  // Notify parent when switching to create tab
  useEffect(() => {
    if (isOpen && onDrawingModeChange) {
      onDrawingModeChange(activeTab === 'create');
    }
  }, [activeTab, isOpen, onDrawingModeChange]);

  // Fetch existing zones for selection (active zones only)
  const { data: zonesData, refetch, isLoading } = useQuery({
    queryKey: ['zones', siteId, 'active'],
    queryFn: async () => {
      const response = await zonesAPI.getZones({ siteId, isActive: 'true' });
      return response.data || [];
    },
    enabled: isOpen && activeTab === 'select' && !!siteId,
    staleTime: 0, // Always refetch when tab switches to select
  });

  // Refetch zones when switching back to select tab
  useEffect(() => {
    if (isOpen && activeTab === 'select') {
      refetch();
    }
  }, [activeTab, isOpen, refetch]);

  // Ensure zones is an array
  const allZones = Array.isArray(zonesData) ? zonesData : [];

  // Filter out already assigned zones
  const availableZones = allZones.filter(
    zone => !existingZoneIds?.includes(zone.id)
  );

  const handleSelectZone = (zone) => {
    const hasPolygon = zone?.polygon?.coordinates && zone.polygon.coordinates.length > 0;

    // Call parent handler with zone details
    onZoneAssigned({
      ...zone,
      source: 'existing',
      dbId: zone.id,
    });
    onClose();

    // Show appropriate message based on whether polygon exists
    if (hasPolygon) {
      toast.success(`Zone "${zone.name}" assigned to camera`);
    } else {
      toast.success(`Zone "${zone.name}" assigned! You can now draw its polygon on the camera canvas.`, {
        duration: 4000,
      });
    }
  };

  const handleCreateZone = () => {
    if (!newZoneName.trim()) {
      toast.error('Please enter a zone name');
      return;
    }

    if (!currentPoints || currentPoints.length < 3) {
      toast.error('Please draw a polygon with at least 3 points');
      return;
    }

    // Create local zone object
    const newZone = {
      id: `zone_${Date.now()}`,
      name: newZoneName,
      type: selectedZoneType,
      points: currentPoints,
      source: 'new',
      alertOnEntry: true,
    };

    onZoneAssigned(newZone);

    // Reset form
    setNewZoneName('');
    setCurrentPoints([]);
    setSelectedZoneType('restricted');
    setActiveTab('select');
    onClose();
    toast.success('Zone created and assigned to camera');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop to close modal */}
      <div
        className="fixed inset-0 z-30 bg-black/20"
        onClick={onClose}
        style={{ pointerEvents: 'auto' }}
      />
      {/* Modal Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-80 bg-white shadow-2xl flex flex-col overflow-hidden z-40 border-l border-slate-200">
        <div
          className="flex flex-col h-full"
          onClick={(e) => e.stopPropagation()}
          style={{ pointerEvents: 'auto' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-white flex-shrink-0">
            <h3 className="text-lg font-semibold text-slate-900">Assign/Create Zone</h3>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
              <XMarkIcon className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 bg-slate-50">
            <button
              onClick={() => setActiveTab('select')}
              className={clsx(
                'flex-1 px-4 py-3 text-sm font-medium transition-colors',
                activeTab === 'select'
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-white'
                  : 'text-slate-600 hover:text-slate-800'
              )}
            >
              📋 Select Existing Zone
            </button>
            <button
              onClick={() => setActiveTab('create')}
              className={clsx(
                'flex-1 px-4 py-3 text-sm font-medium transition-colors',
                activeTab === 'create'
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-white'
                  : 'text-slate-600 hover:text-slate-800'
              )}
            >
              ✏️ Create New Zone
            </button>
          </div>

          {/* Info banner for create tab */}
          {activeTab === 'create' && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mx-6 mt-4">
              <p className="text-sm text-blue-800">
                💡 <strong>Draw on the camera preview above</strong> to define your zone polygon.
                Click multiple points to create a shape (minimum 3 points).
              </p>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'select' ? (
              <ZoneSelector
                zones={availableZones}
                onSelectZone={handleSelectZone}
                isLoading={isLoading}
              />
            ) : (
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Zone Name *
                  </label>
                  <input
                    type="text"
                    value={newZoneName}
                    onChange={(e) => setNewZoneName(e.target.value)}
                    placeholder="e.g., Loading Bay A"
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Zone Type
                  </label>
                  <select
                    value={selectedZoneType}
                    onChange={(e) => setSelectedZoneType(e.target.value)}
                    className="input"
                  >
                    {ZONE_TYPES.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Draw Polygon on Camera *
                  </label>
                  <div className="bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                    <div className="text-sm text-slate-600">
                      {currentPoints && currentPoints.length > 0 ? (
                        <>
                          <p className="font-medium text-blue-600">
                            {currentPoints.length} points added
                          </p>
                          <p className="text-xs mt-1">
                            {currentPoints.length < 3
                              ? `Add ${3 - currentPoints.length} more point(s) to complete polygon`
                              : 'Polygon ready! Click "Create Zone" below to save'}
                          </p>
                        </>
                      ) : (
                        <p>
                          Draw on the camera preview above by clicking points
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {currentPoints && currentPoints.length > 0 && (
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentPoints([]);
                        setNewZoneName('');
                      }}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      ← Clear and Start Over
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 p-4 border-t border-slate-200 bg-white flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors font-medium text-sm"
              disabled={isCreating}
            >
              Cancel
            </button>
            {activeTab === 'create' && (
              <button
                type="button"
                onClick={handleCreateZone}
                disabled={isCreating || !newZoneName.trim() || (currentPoints && currentPoints.length < 3)}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : 'Create Zone'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
